import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';
const CalendarToday = lazy(() => import('@mui/icons-material/CalendarToday'));
const DirectionsCar = lazy(() => import('@mui/icons-material/DirectionsCar'));
const Receipt = lazy(() => import('@mui/icons-material/Receipt'));
const Payment = lazy(() => import('@mui/icons-material/Payment'));
const People = lazy(() => import('@mui/icons-material/People'));
const AcUnit = lazy(() => import('@mui/icons-material/AcUnit'));
const Plumbing = lazy(() => import('@mui/icons-material/Plumbing'));
const ElectricBolt = lazy(() => import('@mui/icons-material/ElectricBolt'));
const CleaningServices = lazy(() => import('@mui/icons-material/CleaningServices'));
const Garage = lazy(() => import('@mui/icons-material/Garage'));
const Article = lazy(() => import('@mui/icons-material/Article'));
const Help = lazy(() => import('@mui/icons-material/Help'));
const Code = lazy(() => import('@mui/icons-material/Code'));
const Handshake = lazy(() => import('@mui/icons-material/Handshake'));
const Business = lazy(() => import('@mui/icons-material/Business'));
const Work = lazy(() => import('@mui/icons-material/Work'));
const ContactSupport = lazy(() => import('@mui/icons-material/ContactSupport'));
const Newspaper = lazy(() => import('@mui/icons-material/Newspaper'));
const AccessTime = lazy(() => import('@mui/icons-material/AccessTime'));
const AccountTree = lazy(() => import('@mui/icons-material/AccountTree'));
const Extension = lazy(() => import('@mui/icons-material/Extension'));
const HelpCircleOutline = lazy(() => import('@mui/icons-material/HelpOutline'));


const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  z-index: 100;
  background-color: ${props => props.scrolled ? '#ffffff' : 'transparent'};
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'};
  transition: all 0.3s ease;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
`;

const Logo = styled(motion(Link))`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.scrolled ? '#1e293b' : '#fff'};
  text-decoration: none;
  z-index: 100;
`;

const NavLinks = styled(motion.div)`
  display: ${props => props.isLoading ? 'none' : 'flex'};
  gap: 2rem;
  align-items: center;
  will-change: opacity;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: static;
  cursor: pointer;

  &:hover > .dropdown {
    opacity: 1;
    visibility: visible;
  }
`;

const NavLink = styled(motion(Link))`
  color: ${props => props.scrolled ? '#1e293b' : '#fff'};
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const LoginButton = styled(Link)`
  color: ${props => props.scrolled ? '#1e293b' : '#fff'};
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  border: 2px solid ${props => props.scrolled ? '#1e293b' : '#fff'};
  transition: all 0.3s ease;
  margin-right: 1rem;

  &:hover {
    background: ${props => props.scrolled ? '#1e293b' : '#fff'};
    color: ${props => props.scrolled ? '#fff' : '#4f46e5'};
  }
`;

const CTAButton = styled(Link)`
  background: #4f46e5;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    background: #4338ca;
  }
`;

const Dropdown = styled(motion.ul)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  padding: 3rem 0;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 101;
`;

const DropdownContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  padding: 0 2rem;
`;

const DropdownItem = styled(motion.li)`
  display: flex;
  align-items: center;
  padding: 1.25rem;
  color: #1f2937;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;

  .icon {
    color: #4f46e5;
    font-size: 1.25rem;
    width: 40px;
    height: 40px;
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.75rem;
    border-radius: 8px;
    background: rgba(79, 70, 229, 0.1);
    transition: all 0.2s ease;
  }

  .content {
    flex: 1;
  }

  .title {
    font-weight: 500;
    font-size: 1rem;
    margin-bottom: 0.25rem;
    color: #1f2937;
  }

  .description {
    color: #6b7280;
    font-size: 0.85rem;
  }

  &:hover {
    background: #f3f4f6;
    
    .icon {
      background: rgba(79, 70, 229, 0.2);
      transform: scale(1.1);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.scrolled ? '#1e293b' : '#ffffff'};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease;
  z-index: 999;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 400px;
  height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: 4rem 0 1rem 0;
  box-shadow: -4px 0 25px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const MobileNavItem = styled.div`
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const MobileNavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.theme.colors.text.primary};
  
  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const MobileDropdown = styled.div`
  background: ${props => props.theme.isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'};
  padding: 0.5rem 0;
`;

const MobileMenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1.5rem;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9375rem;

  .icon {
    display: flex;
    align-items: center;
    color: #6b7280;
  }

  &:hover {
    background: ${props => props.theme.isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'};
    color: #4f46e5;
  }
`;

const MobileButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem;
  margin-top: auto;
  border-top: 1px solid ${props => props.theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
`;

const MobileCTAButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem;
  background: #4f46e5;
  color: white;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  border: none;
  width: 100%;
  
  &:hover {
    background: #4338ca;
    transform: translateY(-1px);
  }
`;

const MobileLoginButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem;
  background: transparent;
  color: ${props => props.theme.colors.text.primary};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    border-color: #4f46e5;
    color: #4f46e5;
    background: ${props => props.theme.isDark ? 'rgba(79, 70, 229, 0.1)' : 'rgba(79, 70, 229, 0.05)'};
  }
`;

const NavPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(79, 70, 229, 0.1);
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    background: rgba(79, 70, 229, 0.2);
    transform: scale(1.1);
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1002;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const mobileMenuVariants = {
  open: { 
    x: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  closed: { 
    x: "100%",
    transition: {
      duration: 0.2,
      ease: 'easeIn'
    }
  }
};

class NavbarErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Nav>
          <Container>
            <Logo to="/">ServTrackr</Logo>
          </Container>
        </Nav>
      );
    }

    return this.props.children;
  }
}

const Navbar = ({ scrolled }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  useEffect(() => {
    const preloadIcons = async () => {
      const iconPromises = Object.values(menuItems).flat().map(item => {
        if (item.icon) {
          return new Promise(resolve => {
            requestAnimationFrame(resolve);
          });
        }
        return Promise.resolve();
      });
      
      await Promise.all(iconPromises);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      
      return () => clearTimeout(timer);
    };

    preloadIcons();
  }, []);

  const menuItems = useMemo(() => ({
    Features: [
      { 
        icon: <IconWrapper><CalendarToday /></IconWrapper>, 
        text: 'Job Scheduling', 
        description: 'Manage appointments',
        path: '/features/job-scheduling'
      },
      { 
        icon: <IconWrapper><DirectionsCar /></IconWrapper>, 
        text: 'Dispatch', 
        description: 'Fleet tracking',
        path: '/features/dispatch'
      },
      { 
        icon: <IconWrapper><Receipt /></IconWrapper>, 
        text: 'Invoicing', 
        description: 'Billing management',
        path: '/features/invoicing'
      },
      { 
        icon: <IconWrapper><Payment /></IconWrapper>, 
        text: 'Payments', 
        description: 'Process payments',
        path: '/features/payments'
      },
      { 
        icon: <IconWrapper><People /></IconWrapper>, 
        text: 'Customer Management', 
        description: 'Build lasting customer relationships',
        path: '/features/customer-management'
      },
      { 
        icon: <IconWrapper><AccessTime /></IconWrapper>, 
        text: 'Smart Timesheets', 
        description: 'Automated time tracking',
        path: '/features/smart-timesheets'
      },
      { 
        icon: <IconWrapper><AccountTree /></IconWrapper>, 
        text: 'Business Ecosystem', 
        description: 'End-to-end business workflow',
        path: '/features/ecosystem'
      },
      { 
        icon: <IconWrapper><Extension /></IconWrapper>, 
        text: 'And More', 
        description: 'Additional powerful features',
        path: '/features/and-more'
      }
    ],
    Industries: [
      { 
        icon: <IconWrapper><AcUnit /></IconWrapper>, 
        text: 'HVAC', 
        description: 'Streamline your HVAC operations efficiently',
        path: '/industries/hvac'
      },
      { 
        icon: <IconWrapper><Plumbing /></IconWrapper>, 
        text: 'Plumbing', 
        description: 'Run your plumbing business more efficiently',
        path: '/industries/plumbing'
      },
      { 
        icon: <IconWrapper><ElectricBolt /></IconWrapper>, 
        text: 'Electrical', 
        description: 'Power your electrical business to success',
        path: '/industries/electrical'
      },
      { 
        icon: <IconWrapper><CleaningServices /></IconWrapper>, 
        text: 'Cleaning', 
        description: 'Transform your cleaning service operations',
        path: '/industries/cleaning'
      },
      { 
        icon: <IconWrapper><Garage /></IconWrapper>, 
        text: 'Garage Door', 
        description: 'Elevate your garage door service business',
        path: '/industries/garage-door'
      },
      { 
        icon: <IconWrapper><DirectionsCar /></IconWrapper>, 
        text: 'Auto Body', 
        description: 'Drive your auto body shop forward',
        path: '/industries/auto-gas'
      },
      { 
        icon: <IconWrapper><AcUnit /></IconWrapper>, 
        text: 'Snow Removal', 
        description: 'Optimize your snow removal operations',
        path: '/industries/snow-removal'
      },
      { 
        icon: <IconWrapper><Extension /></IconWrapper>, 
        text: 'And More Industries', 
        description: 'Tailored solutions for every service business',
        path: '/industries/and-more'
      }
    ],
    Resources: [
      { 
        icon: <IconWrapper><Article /></IconWrapper>, 
        text: 'Blog', 
        description: 'Latest industry insights',
        path: '/resources/blog'
      },
      { 
        icon: <IconWrapper><Help /></IconWrapper>, 
        text: 'Help Center', 
        description: 'Support and documentation',
        path: '/resources/help-center'
      },
      { 
        icon: <IconWrapper><Code /></IconWrapper>, 
        text: 'API Documentation', 
        description: 'Developer resources',
        path: '/resources/api-documentation'
      },
      { 
        icon: <IconWrapper><Handshake /></IconWrapper>, 
        text: 'Partner Program', 
        description: 'Join our partner network',
        path: '/resources/partner-program'
      }
    ],
    Company: [
      { 
        icon: <IconWrapper><Business /></IconWrapper>, 
        text: 'About Us', 
        description: 'Our story and mission',
        path: '/company/about-us'
      },
      { 
        icon: <IconWrapper><Work /></IconWrapper>, 
        text: 'Careers', 
        description: 'Join our growing team',
        path: '/company/careers'
      },
      { 
        icon: <IconWrapper><ContactSupport /></IconWrapper>, 
        text: 'Contact', 
        description: 'Get in touch with us',
        path: '/company/contact'
      },
      { 
        icon: <IconWrapper><Newspaper /></IconWrapper>, 
        text: 'Press', 
        description: 'Latest news and updates',
        path: '/company/press'
      }
    ]
  }), []);

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const closeMobileMenu = () => {
    setIsOpen(false);
    setOpenCategories({});
  };

  return (
    <Nav scrolled={scrolled}>
      <Container>
        <Logo to="/" scrolled={scrolled}>
          ServTrackr
        </Logo>

        {isLoading ? (
          <NavPlaceholder>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              transition={{ duration: 0.2 }}
              style={{
                height: "40px",
                width: "300px",
                background: scrolled ? "#f3f4f6" : "rgba(255,255,255,0.1)",
                borderRadius: "8px"
              }}
            />
          </NavPlaceholder>
        ) : (
          <Suspense fallback={<NavPlaceholder />}>
            <NavLinks isLoading={isLoading}>
              {Object.entries(menuItems).map(([category, items]) => (
                <NavItem key={category}>
                  <NavLink to="#" scrolled={scrolled}>
                    {category} <FiChevronDown />
                  </NavLink>
                  <Dropdown className="dropdown">
                    <DropdownContent>
                      {items.map(item => (
                        <DropdownItem 
                          key={item.text} 
                          to={item.path}
                          as={Link}
                          onClick={() => {
                            setIsOpen(false);
                          }}
                        >
                          <span className="icon">{item.icon}</span>
                          <div className="content">
                            <div className="title">{item.text}</div>
                            <div className="description">{item.description}</div>
                          </div>
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                </NavItem>
              ))}
              <NavLink 
                to="/helpdesk" 
                scrolled={scrolled}
                as={Link}
              >
                Support
              </NavLink>
              <CTAButton to="https://app.servtrackr.com/register">Free Trial</CTAButton>
              <LoginButton to="https://app.servtrackr.com/login" scrolled={scrolled}>Login</LoginButton>
            </NavLinks>
          </Suspense>
        )}

        <MobileMenuButton 
          onClick={() => setIsOpen(!isOpen)} 
          scrolled={scrolled}
        >
          <FaBars />
        </MobileMenuButton>
      </Container>

      <MobileMenuOverlay
        isOpen={isOpen}
        onClick={closeMobileMenu}
        initial={false}
        animate={isOpen ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      <MobileMenu
        isOpen={isOpen}
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={mobileMenuVariants}
      >
        <CloseButton onClick={closeMobileMenu} aria-label="Close menu">
          <FaTimes />
        </CloseButton>

        {Object.entries(menuItems).map(([category, items]) => (
          <MobileNavItem key={category}>
            <MobileNavHeader 
              onClick={() => toggleCategory(category)}
              $isOpen={openCategories[category]}
            >
              {category}
              <FiChevronDown />
            </MobileNavHeader>
            
            {openCategories[category] && (
              <MobileDropdown>
                {items.map(item => (
                  <MobileMenuItem 
                    key={item.text}
                    to={item.path}
                    onClick={() => {
                      setIsOpen(false);
                      setOpenCategories({});
                    }}
                  >
                    <span className="icon">{item.icon}</span>
                    {item.text}
                  </MobileMenuItem>
                ))}
              </MobileDropdown>
            )}
          </MobileNavItem>
        ))}
        
        <MobileNavItem>
          <MobileMenuItem
            to="/helpdesk"
            onClick={() => {
              setIsOpen(false);
              setOpenCategories({});
            }}
          >
            <span className="icon">
              <Suspense fallback={<div>...</div>}>
                <HelpCircleOutline />
              </Suspense>
            </span>
            Support
          </MobileMenuItem>
        </MobileNavItem>

        <MobileButtons>
          <MobileCTAButton to="/free-trial" onClick={closeMobileMenu}>
            Start Free Trial
          </MobileCTAButton>
          <MobileLoginButton to="https://app.servtrackr.com/sign-up" onClick={closeMobileMenu}>
            Sign In
          </MobileLoginButton>
        </MobileButtons>
      </MobileMenu>
    </Nav>
  );
};

export default function NavbarWithErrorBoundary() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <NavbarErrorBoundary>
      <Navbar scrolled={scrolled} />
    </NavbarErrorBoundary>
  );
}
